import React, { useState } from 'react';
import { UserPlus, Shield, ArrowLeft, Sparkles, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { validateRegisterForm, getPasswordStrength } from '../utils/validation';
import { sendVerificationCode, verifyCode } from '../utils/emailVerification';
interface PatientSignupPageProps {
  onBackToLogin: () => void;
}
export function PatientSignupPage({
  onBackToLogin
}: PatientSignupPageProps) {
  const {
    register
  } = useAuth();
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const passwordStrength = password ? getPasswordStrength(password) : null;
  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500',
    very_strong: 'bg-emerald-500'
  };
  const handleSendCode = async () => {
    setRegisterError('');
    setVerificationError('');
    // Validate form first
    const validation = validateRegisterForm(name, email, password, confirmPassword);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});
    setIsSendingCode(true);
    // Send verification code
    const result = await sendVerificationCode(email);
    if (result.success) {
      setCodeSent(true);
      setStep('verify');
    } else {
      setRegisterError(result.error || 'Error al enviar código de verificación');
    }
    setIsSendingCode(false);
  };
  const handleVerifyCode = async () => {
    setVerificationError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Por favor ingresa el código de 6 dígitos');
      return;
    }
    setIsVerifying(true);
    const result = await verifyCode(email, verificationCode);
    if (result.success) {
      // Code verified, proceed with registration
      await handleRegister();
    } else {
      setVerificationError(result.error || 'Código incorrecto');
    }
    setIsVerifying(false);
  };
  const handleRegister = async () => {
    setIsLoading(true);
    // Register with 'paciente' role
    const result = await register(name, email, password, 'paciente');
    if (!result.success) {
      setRegisterError(result.error || 'Error al crear la cuenta');
      setStep('form');
    }
    setIsLoading(false);
  };
  const handleResendCode = async () => {
    setVerificationError('');
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setVerificationError('');
      setVerificationCode('');
    } else {
      setVerificationError(result.error || 'Error al reenviar código');
    }
    setIsSendingCode(false);
  };
  // Verification Step
  if (step === 'verify') {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4" data-id="element-4384">
        <div className="w-full max-w-md" data-id="element-4385">
          {/* Back Button */}
          <button onClick={() => setStep('form')} className="mb-6 flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors" data-id="element-4386">
            <ArrowLeft className="w-4 h-4" data-id="element-4387" />
            <span className="text-sm font-medium" data-id="element-4388">Volver</span>
          </button>

          {/* Verification Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-teal-100" data-id="element-4389">
            <div className="text-center mb-8" data-id="element-4390">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-xl mb-4" data-id="element-4391">
                <Mail className="w-10 h-10 text-white" strokeWidth={2} data-id="element-4392" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4393">
                Verifica tu correo
              </h2>
              <p className="text-sm text-gray-600" data-id="element-4394">
                Hemos enviado un código de 6 dígitos a
              </p>
              <p className="text-sm font-semibold text-teal-600 mt-1" data-id="element-4395">
                {email}
              </p>
            </div>

            {verificationError && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg" data-id="element-4396">
                <div className="flex items-start gap-3" data-id="element-4397">
                  <div className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5" data-id="element-4398">
                    <span className="text-red-600 text-xs font-bold" data-id="element-4399">!</span>
                  </div>
                  <p className="text-sm text-red-700 font-medium" data-id="element-4400">
                    {verificationError}
                  </p>
                </div>
              </div>}

            <div className="space-y-6" data-id="element-4401">
              <div data-id="element-4402">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4403">
                  Código de Verificación
                </label>
                <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-4 text-center text-2xl font-bold tracking-widest border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-gray-50" placeholder="000000" data-id="element-4404" />
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-4405">
                  El código expira en 10 minutos
                </p>
              </div>

              <Button onClick={handleVerifyCode} disabled={isVerifying || verificationCode.length !== 6} className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 shadow-lg shadow-teal-500/25 rounded-xl transition-all duration-300 transform hover:-translate-y-0.5" isLoading={isVerifying || isLoading} data-id="element-4406">
                {isLoading ? 'Creando cuenta...' : 'Verificar y Crear Cuenta'}
              </Button>

              <div className="text-center" data-id="element-4407">
                <p className="text-sm text-gray-600 mb-2" data-id="element-4408">
                  ¿No recibiste el código?
                </p>
                <button onClick={handleResendCode} disabled={isSendingCode} className="text-teal-600 font-semibold hover:text-teal-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4409">
                  {isSendingCode ? 'Reenviando...' : 'Reenviar código'}
                </button>
              </div>
            </div>
          </div>

          {/* Security Note */}
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-4410">
            <Shield className="w-4 h-4 text-teal-600" data-id="element-4411" />
            <span data-id="element-4412">Verificación segura de correo electrónico</span>
          </div>
        </div>
      </div>;
  }
  // Registration Form Step
  return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4" data-id="element-4413">
      <div className="w-full max-w-lg" data-id="element-4414">
        {/* Back Button */}
        <button onClick={onBackToLogin} className="mb-6 flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors" data-id="element-4415">
          <ArrowLeft className="w-4 h-4" data-id="element-4416" />
          <span className="text-sm font-medium" data-id="element-4417">
            Volver al inicio de sesión
          </span>
        </button>

        {/* Logo and Title */}
        <div className="text-center mb-8" data-id="element-4418">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-xl mb-4" data-id="element-4419">
            <UserPlus className="w-10 h-10 text-white" strokeWidth={2} data-id="element-4420" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4421">
            Crear Cuenta de Paciente
          </h1>
          <div className="flex items-center justify-center gap-2 text-teal-600" data-id="element-4422">
            <Sparkles className="w-4 h-4" data-id="element-4423" />
            <p className="text-sm font-medium" data-id="element-4424">Sistema Savidna</p>
            <Sparkles className="w-4 h-4" data-id="element-4425" />
          </div>
        </div>

        {/* Register Form */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-teal-100" data-id="element-4426">
          <div className="mb-6" data-id="element-4427">
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-4428">
              Bienvenido
            </h2>
            <p className="text-sm text-gray-600" data-id="element-4429">
              Crea tu cuenta para registrar tu información médica de forma
              segura
            </p>
          </div>

          {registerError && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg" data-id="element-4430">
              <div className="flex items-start gap-3" data-id="element-4431">
                <div className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5" data-id="element-4432">
                  <span className="text-red-600 text-xs font-bold" data-id="element-4433">!</span>
                </div>
                <p className="text-sm text-red-700 font-medium" data-id="element-4434">
                  {registerError}
                </p>
              </div>
            </div>}

          <form onSubmit={e => {
          e.preventDefault();
          handleSendCode();
        }} className="space-y-5" data-id="element-4435">
            <Input type="text" label="Nombre Completo" placeholder="Juan Pérez" value={name} onChange={e => setName(e.target.value)} error={errors.name} className="h-12 bg-gray-50 border-gray-200 focus:ring-teal-500 focus:border-teal-500" data-id="element-4436" />

            <Input type="email" label="Correo Electrónico" placeholder="correo@ejemplo.com" value={email} onChange={e => setEmail(e.target.value)} error={errors.email} className="h-12 bg-gray-50 border-gray-200 focus:ring-teal-500 focus:border-teal-500" data-id="element-4437" />

            <div data-id="element-4438">
              <Input type="password" label="Contraseña" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} error={errors.password} className="h-12 bg-gray-50 border-gray-200 focus:ring-teal-500 focus:border-teal-500" data-id="element-4439" />
              {password && passwordStrength && <div className="mt-2" data-id="element-4440">
                  <div className="flex items-center gap-2 mb-1" data-id="element-4441">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-4442">
                      <div className={`h-full rounded-full transition-all duration-300 ${strengthColors[passwordStrength.level]}`} style={{
                    width: `${Math.min(100, passwordStrength.score / 7 * 100)}%`
                  }} data-id="element-4443" />
                    </div>
                    <span className={`text-xs font-medium ${passwordStrength.level === 'weak' ? 'text-red-500' : passwordStrength.level === 'medium' ? 'text-yellow-500' : 'text-green-500'}`} data-id="element-4444">
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-4445">
                    Mín. 8 caracteres, mayúscula, minúscula, número y carácter
                    especial
                  </p>
                </div>}
            </div>

            <Input type="password" label="Confirmar Contraseña" placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} error={errors.confirmPassword} className="h-12 bg-gray-50 border-gray-200 focus:ring-teal-500 focus:border-teal-500" data-id="element-4446" />

            {/* Security notice */}
            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-xl" data-id="element-4447">
              <Shield className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-4448" />
              <div data-id="element-4449">
                <p className="text-xs text-teal-900 font-medium mb-1" data-id="element-4450">
                  Información Segura y Confidencial
                </p>
                <p className="text-xs text-teal-700" data-id="element-4451">
                  Tu contraseña se almacena de forma segura con cifrado SHA-256.
                  Toda tu información médica está protegida.
                </p>
              </div>
            </div>

            <Button type="submit" className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 shadow-lg shadow-teal-500/25 rounded-xl transition-all duration-300 transform hover:-translate-y-0.5" isLoading={isSendingCode} data-id="element-4452">
              {isSendingCode ? 'Enviando código...' : 'Continuar'}
            </Button>
          </form>

          <div className="mt-6 text-center" data-id="element-4453">
            <p className="text-sm text-gray-600" data-id="element-4454">
              ¿Ya tienes una cuenta?{' '}
              <button onClick={onBackToLogin} className="text-teal-600 font-semibold hover:text-teal-700 hover:underline" data-id="element-4455">
                Iniciar sesión
              </button>
            </p>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-xs text-gray-500 mt-6 max-w-md mx-auto" data-id="element-4456">
          Al crear una cuenta, recibirás un código de verificación en tu correo
          para confirmar tu identidad.
        </p>
      </div>
    </div>;
}